/**
 * Friends driving live, inside a Discord server: what the games and the server's room (worker/live.ts) say to each
 * other. No video: a driver's game sends its inputs (the packed frames a lap's recording keeps) a few times a
 * second, only while someone else in the server is watching or driving too, and every other game drives the same
 * car with them, exactly, a moment behind (src/game/liveWatch.ts): to watch it, or to see it on the track beside
 * your own car as you race. Everything that arrives from a game is checked here first.
 */

/** How often a driver's game sends what it has driven since (ms). */
export const LIVE_FLUSH_MS = 250;
/** A watcher plays this many ticks behind the newest inputs (half a second): room for the next batch to arrive. */
export const LIVE_BEHIND = 30;
/** At most this many ticks in one message: a long run is sent again in pieces. */
export const LIVE_CHUNK = 12_000;
/** A message from a game is never longer than this (a piece of a long run of analog inputs is about 64 KB). */
export const LIVE_MAX_BYTES = 200_000;

/** A driver's run, as their game reports it whenever it changes. */
export interface LiveDrive {
  /** A new number at each restart (their car back on the grid). */
  run: number;
  /** The circuit and car, `date|track hash|car hash`: only drivers on yours can be watched. */
  day: string;
  /** Which lap of the run (0: not across the line yet). */
  lap: number;
  paused: boolean;
  /** Their best lap today: its time and sectors (for the gaps and colours on their clock). */
  best: { ms: number; sectors: number[] } | null;
  /** Yesterday's winner: their car is gold today. */
  gold: boolean;
}

/** A driver as the room shows them: a key for this visit (never their player id) and their name. */
export interface LiveDriver extends LiveDrive {
  key: string;
  name: string;
}

/** Someone watching, and whom (their key), or null: in the room, not watching. */
export interface LiveWatcher {
  key: string;
  name: string;
  focus: string;
}

/** Who's in the room: the drivers, and who's watching whom. `you`: your own key. */
export interface LiveRoster {
  you: string;
  drivers: LiveDriver[];
  watchers: LiveWatcher[];
}

/** From a game to the room. */
export type LiveUp =
  /** Driving (or paused mid-run), or not (null). */
  | { t: 'drive'; drive: LiveDrive | null }
  /** Inputs from tick `from` of run `run` (encodeInputs). */
  | { t: 'in'; run: number; from: number; d: string }
  /** Watching a driver (their key), or not. */
  | { t: 'watch'; focus: string | null }
  /** A watcher is missing some inputs: drivers send their run again from the start. */
  | { t: 'need' }
  /** Whether the server's channel shows you while you drive (src/net/liveMessage.ts): the game's switch. */
  | { t: 'announce'; on: boolean };

/** From the room to a game. */
export type LiveDown = ({ t: 'roster' } & LiveRoster) | { t: 'in'; key: string; run: number; from: number; d: string } | { t: 'need' };

/** What the room keeps for each game connected to it. */
export interface LiveSeat {
  key: string;
  name: string;
  drive: LiveDrive | null;
  focus: string | null;
}

const DAY = /^\d{4}-\d{2}-\d{2}\|[0-9a-f]{16}\|[0-9a-f]{16}$/;
const KEY = /^[0-9a-z]{6,12}$/;
const int = (v: unknown, max: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max;

function drive(v: unknown): LiveDrive | null | undefined {
  if (v === null) return null;
  if (!v || typeof v !== 'object') return undefined;
  const d = v as Record<string, unknown>;
  if (!int(d.run, 2 ** 31) || typeof d.day !== 'string' || !DAY.test(d.day) || !int(d.lap, 99_999) || typeof d.paused !== 'boolean' || typeof d.gold !== 'boolean') return undefined;
  let best: LiveDrive['best'] = null;
  if (d.best !== null) {
    const b = d.best as { ms?: unknown; sectors?: unknown } | undefined;
    if (!b || !int(b.ms, 3_600_000) || !Array.isArray(b.sectors) || b.sectors.length !== 3 || !b.sectors.every((s) => int(s, 3_600_000))) return undefined;
    best = { ms: b.ms, sectors: b.sectors.slice() as number[] };
  }
  return { run: d.run, day: d.day, lap: d.lap, paused: d.paused, best, gold: d.gold };
}

/** A game's message to the room, checked (null: not one). */
export function parseUp(raw: unknown): LiveUp | null {
  if (typeof raw !== 'string' || raw.length > LIVE_MAX_BYTES) return null;
  let m: Record<string, unknown>;
  try {
    m = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!m || typeof m !== 'object') return null;
  switch (m.t) {
    case 'drive': {
      const d = drive(m.drive);
      return d === undefined ? null : { t: 'drive', drive: d };
    }
    case 'in':
      return int(m.run, 2 ** 31) && int(m.from, 60 * 3600) && typeof m.d === 'string' && m.d.length > 0 ? { t: 'in', run: m.run, from: m.from, d: m.d } : null;
    case 'watch':
      return m.focus === null || (typeof m.focus === 'string' && KEY.test(m.focus)) ? { t: 'watch', focus: m.focus } : null;
    case 'need':
      return { t: 'need' };
    case 'announce':
      return typeof m.on === 'boolean' ? { t: 'announce', on: m.on } : null;
    default:
      return null;
  }
}

/** The room as one game sees it: every driver, and everyone watching (a seat that drives isn't also watching). */
export function rosterFor(seats: readonly LiveSeat[], you: string): LiveRoster {
  const drivers: LiveDriver[] = [];
  const watchers: LiveWatcher[] = [];
  for (const s of seats) {
    if (s.drive) drivers.push({ key: s.key, name: s.name, ...s.drive });
    else if (s.focus) watchers.push({ key: s.key, name: s.name, focus: s.focus });
  }
  return { you, drivers, watchers };
}

/** Someone else is watching or driving (so a driver sends its inputs: they see its car). */
export function anyoneListening(r: LiveRoster): boolean {
  return r.watchers.some((w) => w.key !== r.you) || r.drivers.some((d) => d.key !== r.you);
}

/** Who's watching you (their names), for the tag under your clock. */
export function watchingYou(r: LiveRoster): string[] {
  return r.watchers.filter((w) => w.focus === r.you).map((w) => w.name);
}

/** A run's inputs from `from` on, in messages small enough to send. */
export function inputMessages(run: number, packed: readonly number[], from: number, encode: (p: readonly number[]) => string): LiveUp[] {
  const out: LiveUp[] = [];
  for (let i = from; i < packed.length; i += LIVE_CHUNK) out.push({ t: 'in', run, from: i, d: encode(packed.slice(i, i + LIVE_CHUNK)) });
  return out;
}

/** A new key for a game in the room (shown to the others in place of its player id). */
export function seatKey(random: () => number = Math.random): string {
  let k = '';
  while (k.length < 8) k += Math.floor(random() * 36).toString(36);
  return k;
}
